import type { UseFormRegister } from 'react-hook-form';
import type { Value as PhoneValue } from 'react-phone-number-input';

import { Input, PhoneInput } from '@/shared/ui';

import type { FormFieldConfig, FormSubmitValues, FormWidgetConfig } from './form-widget.types';

import type { CommonInputProps } from './ConfigurableFieldShell';
import { FieldShell, HiddenField } from './ConfigurableFieldShell';

import { formatFieldValue, getFieldDisplay, hasFilledValue } from './form-display';

type ConfigurableReadOnlyFieldProps = {
  commonInputProps: CommonInputProps;
  config: FormWidgetConfig;
  error?: string;
  field: FormFieldConfig;
  fieldDisplay: ReturnType<typeof getFieldDisplay>;
  fieldId: string;
  register: UseFormRegister<FormSubmitValues>;
  value: FormSubmitValues[string];
};

export function ConfigurableReadOnlyField({
  commonInputProps,
  config,
  error,
  field,
  fieldDisplay,
  fieldId,
  register,
  value,
}: ConfigurableReadOnlyFieldProps) {
  const displayValue = hasFilledValue(value) ? formatFieldValue(field, value) : '';

  if (field.type === 'phone') {
    return (
      <FieldShell error={error} field={field} fieldDisplay={fieldDisplay} fieldId={fieldId}>
        <PhoneInput
          id={fieldId}
          value={typeof value === 'string' ? (value as PhoneValue) : undefined}
          onChange={() => undefined}
          defaultCountry={config.country}
          hasError={Boolean(error)}
          readOnly
          autoComplete={field.autocomplete ?? 'tel'}
          aria-describedby={`${fieldId}-error`}
        />
        <HiddenField name={field.name} register={register} value={value} />
      </FieldShell>
    );
  }

  return (
    <FieldShell error={error} field={field} fieldDisplay={fieldDisplay} fieldId={fieldId}>
      <Input
        key={`${field.name}-readonly`}
        {...commonInputProps}
        value={displayValue}
        onChange={() => undefined}
        readOnly
        aria-readonly="true"
      />
      <HiddenField name={field.name} register={register} value={value} />
    </FieldShell>
  );
}
